import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/molecules/empty-state";
import { DataTable } from "@/components/molecules/tables/data-table";
import { useEntityTable, BackofficeAPI } from "@/features/backoffice";
import type { AdminFilterValue, AdminRow } from "@/features/backoffice";
import {
  MagnifyingGlassIcon,
  DownloadSimpleIcon,
  EyeIcon,
  EyeSlashIcon,
} from "@phosphor-icons/react";
import { useState } from "react";
import { toast } from "sonner";

/**
 * The back-office's generic grid.
 *
 * Give it a table name and it renders it: the columns come from the server's
 * schema, the cells from each column's type, the pagination and sorting from
 * the shared `DataTable`, and the filter syntax (`status=failed`,
 * `created_at>2026-01-01`) from the same parser every other table in the app
 * uses. Nothing here knows what `sessions` or `customers` contain.
 */
export function EntityTable({
  table,
  clientId,
  implicitFilters,
  initialSearch,
  defaultPageSize = 50,
  enableDeletedToggle = true,
  hideSearch = false,
  extraToolbar,
}: {
  table: string;
  /** Restrict to one tenant (used by the tenant page's embedded tables). */
  clientId?: string;
  implicitFilters?: Record<string, AdminFilterValue>;
  initialSearch?: string;
  defaultPageSize?: number;
  enableDeletedToggle?: boolean;
  hideSearch?: boolean;
  extraToolbar?: React.ReactNode;
}) {
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [exporting, setExporting] = useState(false);

  const {
    schema,
    columns,
    data,
    total,
    pageCount,
    isLoading,
    isFetching,
    error,
    pagination,
    setPagination,
    sorting,
    setSorting,
    columnVisibility,
    setColumnVisibility,
    globalFilter,
    setGlobalFilter,
    searchOptions,
  } = useEntityTable({
    table,
    clientId,
    implicitFilters,
    includeDeleted,
    defaultPageSize,
    initialSearch,
  });

  const exportCsv = async () => {
    setExporting(true);
    try {
      await BackofficeAPI.exportCsv(table, searchOptions);
    } catch (exportError) {
      toast.error(
        exportError instanceof Error
          ? exportError.message
          : "Export impossible",
      );
    } finally {
      setExporting(false);
    }
  };

  if (error) {
    return (
      <div className="rounded-sm border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
        {error.message}
      </div>
    );
  }

  const softDeletable = !!schema?.columns?.is_deleted;
  // A search that finds nothing among live rows is often looking for a row that was
  // deleted since (an interim turn's messages, a cancelled booking) — offer the other half.
  const searching =
    !!globalFilter.trim() ||
    Object.keys(searchOptions.filters || {}).length >
      Object.keys(implicitFilters || {}).length;
  const emptyState =
    enableDeletedToggle && softDeletable && !includeDeleted && searching ? (
      <EmptyState
        compact
        icon={MagnifyingGlassIcon}
        title="Aucun résultat parmi les lignes actives."
        action={
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIncludeDeleted(true)}
          >
            <EyeIcon className="h-4 w-4 mr-2" />
            Chercher aussi parmi les supprimées
          </Button>
        }
      />
    ) : undefined;

  return (
    <DataTable
      columns={columns}
      data={data as AdminRow[]}
      isLoading={isLoading}
      isFetching={isFetching}
      total={total}
      pageCount={pageCount}
      pagination={pagination}
      setPagination={setPagination}
      sorting={sorting}
      setSorting={setSorting}
      globalFilter={globalFilter}
      setGlobalFilter={setGlobalFilter}
      columnVisibility={columnVisibility}
      setColumnVisibility={setColumnVisibility}
      hideSearch={hideSearch}
      showColumnSettings
      stickyColumnsCount={0}
      emptyState={emptyState}
      onRowNavigate={(row) => {
        const id = schema ? row[schema.id_column] : undefined;
        if (id === undefined || id === null || id === "") return null;
        const value = encodeURIComponent(String(id));
        return table === "clients"
          ? `/admin/tenants/${value}`
          : `/admin/explorer/${table}/${value}`;
      }}
      toolbar={
        <>
          {extraToolbar}
          {enableDeletedToggle && softDeletable && (
            <Button
              variant={includeDeleted ? "secondary" : "outline"}
              size="sm"
              className="h-9"
              onClick={() => setIncludeDeleted((previous) => !previous)}
              title="Afficher aussi les lignes supprimées (soft delete)"
            >
              {includeDeleted ? (
                <EyeIcon className="h-4 w-4 mr-2" />
              ) : (
                <EyeSlashIcon className="h-4 w-4 mr-2" />
              )}
              Supprimées
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            className="h-9"
            onClick={exportCsv}
            disabled={exporting || !total}
          >
            <DownloadSimpleIcon className="h-4 w-4 mr-2" />
            CSV
          </Button>
        </>
      }
    />
  );
}
